import { SOCIAL_PLATFORMS } from "../utils/platform-post-util";

/* ─── API response types ─── */

export interface PlatformAnalytics {
  platform: string;
  social_account_id: string;
  success: boolean;
  platform_url: string;
  failure_reason?: string | null;
  error?: Record<string, unknown> | string | null;
  details?: Record<string, unknown> | string | null;
  metrics: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
  };
  raw_metrics: Record<string, unknown>;
  posted_at: string | null;
  caption: string;
  media: unknown[];
}

export interface PostAnalyticsData {
  aggregate: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
  };
  platforms: PlatformAnalytics[];
}

export interface MetricDef {
  iconName: string;
  label: string;
  value: number;
}

/* ─── Pure helpers ─── */

export const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

export const normalizePlatformId = (id: string): string => {
  const lower = (id ?? "").toLowerCase().replace(/[\s-]+/g, "_");
  if (lower === "twitter" || lower === "x_twitter") return "x";
  if (lower === "fb" || lower === "facebook_page") return "facebook";
  if (
    lower === "linked_in" ||
    lower === "linkedin_page" ||
    lower === "linkedin_organization"
  ) {
    return "linkedin";
  }
  if (lower === "instagram_business" || lower === "ig") return "instagram";
  if (lower === "thread") return "threads";
  return lower;
};

export const getPlatformMeta = (platformId: string) => {
  const normalized = normalizePlatformId(platformId);
  return (
    SOCIAL_PLATFORMS.find((p) => normalizePlatformId(p.id) === normalized) ?? {
      id: normalized,
      name:
        normalized.length > 0
          ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
          : "Unknown",
      icon: "",
    }
  );
};

/* ─── Raw metric readers ─── */

export const rawNum = (raw: Record<string, unknown>, key: string): number => {
  const v = raw[key];
  return typeof v === "number" ? v : Number(v) || 0;
};

export const rawNested = (
  raw: Record<string, unknown>,
  parent: string,
  key: string
): number => {
  const obj = raw[parent];
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const v = (obj as Record<string, unknown>)[key];
    return typeof v === "number" ? v : Number(v) || 0;
  }
  return 0;
};

/* ─── Platform-specific metric definitions ─── */

export const getTwitterMetrics = (raw: Record<string, unknown>): MetricDef[] => [
  { iconName: "Eye", label: "Impressions", value: rawNested(raw, "public_metrics", "impression_count") || rawNested(raw, "organic_metrics", "impression_count") },
  { iconName: "Heart", label: "Likes", value: rawNested(raw, "public_metrics", "like_count") || rawNested(raw, "organic_metrics", "like_count") },
  { iconName: "MessageCircle", label: "Replies", value: rawNested(raw, "public_metrics", "reply_count") || rawNested(raw, "organic_metrics", "reply_count") },
  { iconName: "Repeat2", label: "Retweets", value: rawNested(raw, "public_metrics", "retweet_count") || rawNested(raw, "organic_metrics", "retweet_count") },
  { iconName: "Share2", label: "Quotes", value: rawNested(raw, "public_metrics", "quote_count") },
  { iconName: "Bookmark", label: "Bookmarks", value: rawNested(raw, "public_metrics", "bookmark_count") },
  { iconName: "MousePointerClick", label: "Profile Clicks", value: rawNested(raw, "organic_metrics", "user_profile_clicks") || rawNested(raw, "non_public_metrics", "user_profile_clicks") },
  { iconName: "ExternalLink", label: "URL Clicks", value: rawNested(raw, "organic_metrics", "url_link_clicks") || rawNested(raw, "non_public_metrics", "url_link_clicks") },
];

export const getInstagramMetrics = (raw: Record<string, unknown>): MetricDef[] => [
  { iconName: "Eye", label: "Views", value: rawNum(raw, "views") },
  { iconName: "Heart", label: "Likes", value: rawNum(raw, "likes") },
  { iconName: "MessageCircle", label: "Comments", value: rawNum(raw, "comments") },
  { iconName: "Share2", label: "Shares", value: rawNum(raw, "shares") },
  { iconName: "Bookmark", label: "Saved", value: rawNum(raw, "saved") },
  { iconName: "Target", label: "Reach", value: rawNum(raw, "reach") },
  { iconName: "UserPlus", label: "Follows", value: rawNum(raw, "follows") },
  { iconName: "Activity", label: "Interactions", value: rawNum(raw, "total_interactions") },
];

export const getFacebookMetrics = (raw: Record<string, unknown>): MetricDef[] => [
  { iconName: "Target", label: "Reach", value: rawNum(raw, "reach") },
  { iconName: "ThumbsUp", label: "Reactions", value: rawNum(raw, "reactions_total") },
  { iconName: "MessageCircle", label: "Comments", value: rawNum(raw, "comments") },
  { iconName: "Share2", label: "Shares", value: rawNum(raw, "shares") },
  { iconName: "Users", label: "Organic Reach", value: rawNum(raw, "organic_reach") },
  { iconName: "TrendingUp", label: "Viral Reach", value: rawNum(raw, "viral_reach") },
  { iconName: "Play", label: "Video Views", value: rawNum(raw, "video_views") },
  { iconName: "Eye", label: "Unique Views", value: rawNum(raw, "video_views_unique") },
];

export const getLinkedInMetrics = (raw: Record<string, unknown>): MetricDef[] => [
  { iconName: "Eye", label: "Impressions", value: rawNum(raw, "impressionCount") },
  { iconName: "Heart", label: "Likes", value: rawNum(raw, "likeCount") },
  { iconName: "MessageCircle", label: "Comments", value: rawNum(raw, "commentCount") },
  { iconName: "Share2", label: "Shares", value: rawNum(raw, "shareCount") },
  { iconName: "MousePointerClick", label: "Clicks", value: rawNum(raw, "clickCount") },
  { iconName: "TrendingUp", label: "Engagement", value: rawNum(raw, "engagement") },
  { iconName: "Play", label: "Video Views", value: rawNum(raw, "videoView") },
  { iconName: "Users", label: "Viewers", value: rawNum(raw, "viewer") },
];

export const getPlatformMetricDefs = (p: PlatformAnalytics): MetricDef[] => {
  const raw = p.raw_metrics ?? {};
  const norm = normalizePlatformId(p.platform);
  switch (norm) {
    case "x":
      return getTwitterMetrics(raw);
    case "instagram":
      return getInstagramMetrics(raw);
    case "facebook":
      return getFacebookMetrics(raw);
    case "linkedin":
      return getLinkedInMetrics(raw);
    case "threads":
      return [
        { iconName: "Eye", label: "Views", value: rawNum(raw, "views") || p.metrics.views },
        { iconName: "Heart", label: "Likes", value: rawNum(raw, "likes") || p.metrics.likes },
        { iconName: "MessageCircle", label: "Replies", value: rawNum(raw, "replies") || p.metrics.comments },
        {
          iconName: "Repeat2",
          label: "Reposts",
          value: rawNum(raw, "reposts"),
        },
        {
          iconName: "Share2",
          label: "Quotes",
          value: rawNum(raw, "quotes"),
        },
        {
          iconName: "Send",
          label: "Shares",
          value: rawNum(raw, "shares") || p.metrics.shares,
        },
      ];
    default:
      return [
        { iconName: "Eye", label: "Views", value: p.metrics.views },
        { iconName: "Heart", label: "Likes", value: p.metrics.likes },
        { iconName: "MessageCircle", label: "Comments", value: p.metrics.comments },
        { iconName: "Share2", label: "Shares", value: p.metrics.shares },
      ];
  }
};
